import cn from 'classnames'
import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import { FC, useMemo } from 'react'

import { Nullable } from 'clickNgo/models/utils'
import { canRenderRankIcon } from 'partner360/components/partnerRanks/components/rankIcon/helpers/canRenderRankIcon'
import { RankIcon } from 'partner360/components/partnerRanks/components/rankIcon/RankIcon'
import { BorderDecoration } from 'partner360/features/topPartnersManagement/components/partnerFlags/components/BorderDecoration'
import { shouldUseFlagRequest } from 'partner360/features/topPartnersManagement/helpers/shouldUseFlagRequest'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { TFlagChangeRequest } from 'partner360/models/partnerFlagRequests/TFlagChangeRequest'
import { TFlagChangeRequestStatus } from 'partner360/models/partnerFlagRequests/TFlagChangeRequestStatus'

import styles from './PartnerFlags.module.scss'

interface IPartnerRanksProps {
  rankMask?: number | null
  flagRequests?: Nullable<string>
  requestStatus?: TFlagChangeRequestStatus | null
  availableFlags: IP360PartnerFlags[]
}

export const PartnerFlags: FC<IPartnerRanksProps> = ({
  rankMask,
  flagRequests,
  requestStatus,
  availableFlags,
}) => {
  const shouldUseRequestChanges = shouldUseFlagRequest(requestStatus)

  const parsedFlagRequests = useMemo(() => {
    if (!shouldUseRequestChanges || !flagRequests) {
      return null
    }

    return parseJson<TFlagChangeRequest>(flagRequests)
  }, [flagRequests, shouldUseRequestChanges])

  const preparedRanks = useMemo(() => {
    const requestKeys = parsedFlagRequests ? Object.keys(parsedFlagRequests) : []

    return availableFlags.filter((rank) => {
      const isInMask = Boolean(
        rank.BitValue && rankMask && (rankMask & rank.BitValue) === rank.BitValue
      )

      if (!shouldUseRequestChanges) {
        return isInMask
      }

      const isInRequest = Boolean(rank.FlagKey && requestKeys.includes(rank.FlagKey))

      return isInMask || isInRequest
    })
  }, [availableFlags, rankMask, parsedFlagRequests, shouldUseRequestChanges])

  const visibleRanks = useMemo(() => {
    return preparedRanks.filter((el) => canRenderRankIcon(el))
  }, [preparedRanks])

  return (
    <div className={styles.partnerRanks}>
      {visibleRanks.map((rank) => {
        const requestValue = !shouldUseRequestChanges
          ? undefined
          : parsedFlagRequests?.[rank.FlagKey]

        const isAdd = requestValue === 1
        const isRemove = requestValue === 0

        if (!shouldUseRequestChanges) {
          return (
            <div key={rank.BitValue} className={styles.flagContent}>
              <RankIcon partnerFlag={rank.PartnerFlag} rankConfig={rank} />
            </div>
          )
        }

        return (
          <div
            key={rank.BitValue}
            className={cn(styles.flagWrapper, {
              [styles.withPadding]: isAdd || isRemove,
              [styles.remove]: isRemove,
            })}
          >
            <BorderDecoration isAdd={isAdd} isRemove={isRemove} />

            <div className={styles.flagContent}>
              <RankIcon partnerFlag={rank.PartnerFlag} rankConfig={rank} />
            </div>

            {isAdd && <div className={cn(styles.cornerMark, styles.plus)}>+</div>}

            {isRemove && <div className={cn(styles.cornerMark, styles.cross)}>×</div>}
          </div>
        )
      })}

      {visibleRanks.length === 0 && '-'}
    </div>
  )
}
